import { AlertCircle, CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { Badge } from '../ui/badge';
import { WebhookEventListItemDto } from '../../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '../../lib/utils';

interface WebhookEventCardProps {
  event: WebhookEventListItemDto;
  onClick?: () => void;
  isSelected?: boolean;
}

export function WebhookEventCard({ event, onClick, isSelected }: WebhookEventCardProps) {
  const formattedDate = format(new Date(event.createdAt), 'dd/MM HH:mm:ss', {
    locale: ptBR,
  });

  const getStatusConfig = (status: string) => {
    switch (status.toLowerCase()) {
      case 'processed':
        return {
          variant: 'default' as const,
          icon: <CheckCircle2 className="h-3 w-3" />,
          label: 'OK',
          className: 'bg-green-600 hover:bg-green-700 text-white',
        };
      case 'failed':
        return {
          variant: 'destructive' as const,
          icon: <AlertCircle className="h-3 w-3" />,
          label: 'Erro',
          className: '',
        };
      case 'processing':
        return {
          variant: 'secondary' as const,
          icon: <Loader2 className="h-3 w-3 animate-spin" />,
          label: '...',
          className: '',
        };
      default: // pending
        return {
          variant: 'outline' as const,
          icon: <Clock className="h-3 w-3" />,
          label: 'Pend',
          className: '',
        };
    }
  };

  const statusConfig = getStatusConfig(event.status);

  return (
    <div
      className={cn(
        'flex items-center gap-3 px-3 py-2 rounded-md cursor-pointer transition-colors border border-transparent',
        'hover:bg-muted/50 hover:border-border',
        isSelected && 'bg-primary/10 border-primary/30 hover:bg-primary/15'
      )}
      onClick={onClick}
    >
      {/* Status indicator */}
      <Badge
        variant={statusConfig.variant}
        className={cn('text-xs h-5 px-1.5 shrink-0', statusConfig.className)}
      >
        {statusConfig.icon}
      </Badge>

      {/* Event name */}
      <span className="text-sm font-medium truncate min-w-[80px] max-w-[120px]">
        {event.eventName || 'EVENT'}
      </span>

      {/* Provider & Channel */}
      <div className="flex items-center gap-1 shrink-0">
        <Badge variant="outline" className="text-xs h-5 px-1.5 font-mono">
          {event.provider}
        </Badge>
        {event.channel && (
          <Badge variant="outline" className="text-xs h-5 px-1.5 font-mono">
            {event.channel}
          </Badge>
        )}
      </div>

      {/* Sender ID */}
      <span className="text-xs text-muted-foreground font-mono truncate flex-1 min-w-0">
        {event.senderId || '-'}
      </span>

      {/* Error indicator */}
      {event.errorMessage && (
        <AlertCircle className="h-3.5 w-3.5 text-destructive shrink-0" />
      )}

      {/* Timestamp */}
      <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0 font-mono">
        {formattedDate}
      </span>
    </div>
  );
}
